import { AnyAction, Dispatch } from 'redux'
import { WebSocketContextValue } from '../webSocketConnection/WebSocket'
import { WebSocketRequestMessage_Subscription, WebSocketResponseMessage } from '../../proto/generated/websocket'
import { Candle, Security } from '../../proto/generated/candle-data-depot-common'
import { getSecurityKey } from '../../util/security'
import { getCandlesBulkV2 } from '../../services/apis/candles'
import { getCandleRequestV2, getTimeWidthInMilliSeconds } from '../chartV2/chartHelpers'
import { OptionGreeks, TimeBucket, TimeBucket_TimeSeries } from '../../proto/generated/common'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { GetCandlesRequestV2 } from '../../proto/generated/candle-data-depot'
import websocketActions from '../../actions/websocket.actions'
import { getCandleChange } from './helpers'
import { getNearestTimeBucketStartTimeStampToGivenTime } from '../../util/chart'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { removeCandleChangeBookEntries } from '../../reducer/security.reducer'

export interface CandleChangeProps {
    dispatch: Dispatch<AnyAction>
    ws: WebSocketContextValue | null
    subscriptionTag: string
    timebucket: TimeBucket
    onCandleChangeCallback: (candleChange: Record<string, CandleChange>) => void
    tradingSessions: TradingSession[]
}

export interface CandleChange {
    startTimestamp: number
    security: Security
    ltp: number
    ltpChange: number
    ltpChangePercent: number
    impliedVolatility: number
    optionGreeks: OptionGreeks | undefined
    topBid: number
    topAsk: number
    openInterest: number
    oiChange: number
    volume: number,
    deliveryPercentage: number

}

export type CandleChangeCallbackType = (candleChange: Record<string, CandleChange>) => void
export default class CandleChangeManager {
    dispatch: Dispatch<AnyAction>
    ws: WebSocketContextValue | null
    subscriptionTag: string
    securityToCandles: Record<string, Candle[]> = {}
    securityToCandleChange: Record<string, CandleChange> = {}
    subscribedSecurities: Record<string, {
        security: Security,
        subscriptionCount: number
    }> = {}
    timebucket: TimeBucket
    onCandleChangeCallbacks: CandleChangeCallbackType[]
    websocketListnerAdded = false
    tradingSessions: TradingSession[]

    constructor(props: CandleChangeProps) {
        this.dispatch = props.dispatch
        this.ws = props.ws
        this.subscriptionTag = props.subscriptionTag
        this.timebucket = props.timebucket
        this.onCandleChangeCallbacks = [props.onCandleChangeCallback]
        this.tradingSessions = props.tradingSessions
    }

    onTimeBucketChange(timebucket: TimeBucket, candlesData: Record<string, Candle[]>) {
        this.timebucket = timebucket
        const securities = Object.values(this.subscribedSecurities).map(s => s.security)
        const secKeys = Object.keys(this.subscribedSecurities)
        this.removeSecurities(secKeys)
        this.addSecurities(securities, candlesData)
    }

    addOnCandleChangeCallback(callback: CandleChangeCallbackType) {
        this.onCandleChangeCallbacks.push(callback)
    }

    removeOnCandleChangeCallback(callback: CandleChangeCallbackType) {
        this.onCandleChangeCallbacks = this.onCandleChangeCallbacks.filter(cb => cb !== callback)
    }

    /** Attach WS listener when socket is ready — do not set websocketListnerAdded if ws was still null. */
    ensureWebSocketListener() {
        if (this.websocketListnerAdded || !this.ws) return
        this.ws.addListener(this.onWebSocketMessageReceived)
        this.websocketListnerAdded = true
    }

    setWebSocket(ws: WebSocketContextValue | null) {
        if (this.ws === ws) return
        if (this.websocketListnerAdded && this.ws) {
            this.ws.removeListener(this.onWebSocketMessageReceived)
            this.websocketListnerAdded = false
        }
        this.ws = ws
        this.ensureWebSocketListener()
        if (ws && Object.keys(this.subscribedSecurities).length > 0) {
            this.subscribeSecurityCandles()
        }
    }

    subscribeSecurityCandles() {
        const payload: Partial<WebSocketRequestMessage_Subscription> = {
            candleSubscriptions: [
                {
                    tag: this.subscriptionTag,
                    securities: Object.values(this.subscribedSecurities).map(s => s.security),
                    timeBucket: {
                        timeSeries: TimeBucket_TimeSeries.SECOND,
                        value: 1
                    }
                }
            ]
        }
        websocketActions.setWebsocketSubscribeList(this.dispatch, payload)
        this.ensureWebSocketListener()
    }

    async addSecurity(security: Security) {
        const secKey = getSecurityKey(security.exchange, security.code)
        this.subscribedSecurities[secKey] = {
            security,
            subscriptionCount: this.subscribedSecurities?.[secKey]?.subscriptionCount ? this.subscribedSecurities[secKey].subscriptionCount + 1 : 1
        }
        this.subscribeSecurityCandles()
        const backwardCandleRequests = [getCandleRequestV2(
            2,
            0,
            security,
            {
                timeSeries: this.timebucket.timeSeries,
                timeValue: this.timebucket.value
            } as SuperChartConfiguration_TimeBucket,
            Date.now()
        )]
        const response = await getCandlesBulkV2(
            backwardCandleRequests
        )
        const candles = response.responses[0].backwardCandles
        this.securityToCandles[secKey] = [candles[candles.length - 2], {
            ...candles[candles.length - 1],
            lastTickTimestamp: response.responses[0].lastCandleStoreUpdateTime
        } as Candle]
        if (candles.length > 0) {
            const candleChange = getCandleChange(candles[candles.length - 2], candles[candles.length - 1], security)
            this.securityToCandleChange[secKey] = candleChange
        }
        this.update(this.securityToCandleChange)
    }

    async addSecurities(securities: Security[], candlesData?: Record<string, Candle[]>) {
        const backwardCandleRequests: GetCandlesRequestV2[] = []
        const referenceTime = Date.now()
        if (!candlesData) {
            securities.forEach(security => {
                const secKey = getSecurityKey(security.exchange, security.code)
                this.subscribedSecurities[secKey] = {
                    security,
                    subscriptionCount: this.subscribedSecurities?.[secKey]?.subscriptionCount ? this.subscribedSecurities[secKey].subscriptionCount + 1 : 1
                }
                if (!this.securityToCandles[secKey]) {
                    backwardCandleRequests.push(getCandleRequestV2(1, 0, security,
                        {
                            timeSeries: this.timebucket.timeSeries,
                            timeValue: this.timebucket.value
                        } as SuperChartConfiguration_TimeBucket, referenceTime))
                }
            })
            if (backwardCandleRequests.length) {
                this.subscribeSecurityCandles()
                const response = await getCandlesBulkV2(backwardCandleRequests)
                response.responses.forEach(response => {
                    const security = response.security!
                    const secKey = getSecurityKey(security.exchange, security.code)
                    const candles = response.backwardCandles
                    this.securityToCandles[secKey] = response.backwardCandles
                    if (candles.length > 0) {
                        const candleChange = getCandleChange(candles[candles.length - 2], candles[candles.length - 1], security)
                        this.securityToCandleChange[secKey] = candleChange
                    }
                })
            }
        } else {
            securities.forEach(security => {
                const secKey = getSecurityKey(security.exchange, security.code)
                const existing = this.subscribedSecurities[secKey]
                this.subscribedSecurities[secKey] = {
                    security,
                    subscriptionCount: existing?.subscriptionCount ?? 1
                }
            })
            this.subscribeSecurityCandles()
            this.securityToCandles = {
                ...this.securityToCandles,
                ...candlesData
            }
            Object.keys(candlesData).forEach((secKey) => {
                const candles = this.securityToCandles[secKey]
                if (candles.length > 0) {
                    const candleChange = getCandleChange(candles[candles.length - 2], candles[candles.length - 1], this.subscribedSecurities[secKey].security)
                    this.securityToCandleChange[secKey] = candleChange
                }
            })
        }
        this.update(this.securityToCandleChange)
    }

    removeSecurity(secKey: string, bulk = false) {
        if (this.subscribedSecurities[secKey]?.subscriptionCount > 1) {
            this.subscribedSecurities[secKey].subscriptionCount = this.subscribedSecurities[secKey].subscriptionCount - 1
        } else {
            delete this.securityToCandles[secKey]
            delete this.subscribedSecurities[secKey]
        }
        if (!bulk) this.subscribeSecurityCandles()
    }

    removeSecurities(secKeys: string[]) {
        secKeys.forEach(secKey => {
            this.removeSecurity(secKey, true)
        })
        this.subscribeSecurityCandles()
    }

    onCandleChangeCallbackChange(callback: (candleChange: Record<string, CandleChange>) => void) {
        this.onCandleChangeCallbacks = [callback, ...this.onCandleChangeCallbacks.slice(1)]
    }

    onWebSocketMessageReceived = (data: Uint8Array) => {
        const { candleSubscriptionResponses } = WebSocketResponseMessage.decode(data)
        if (candleSubscriptionResponses && candleSubscriptionResponses.length > 0) {
            const timeWidth = getTimeWidthInMilliSeconds({
                timeSeries: this.timebucket.timeSeries,
                timeValue: this.timebucket.value
            } as SuperChartConfiguration_TimeBucket)
            candleSubscriptionResponses.forEach((response) => {
                const { securityCandles, tag } = response
                if (tag === this.subscriptionTag) {
                    let newCandleTimestamp: number | undefined = undefined
                    securityCandles.forEach(({ candle, security }) => {
                        const key = getSecurityKey(security!.exchange, security!.code)
                        let candleData = this.securityToCandles[key]
                        if (!candleData) return
                        const [lastCandle] = candleData.slice(-1)
                        if (!lastCandle || !candle) return
                        const previousTime = lastCandle.startTimestamp
                        const previousLastTickTime = (lastCandle as any)?.lastTickTimestamp ?? lastCandle.startTimestamp
                        const newTime = getNearestTimeBucketStartTimeStampToGivenTime(this.tradingSessions, candle.startTimestamp, {
                            timeSeries: this.timebucket.timeSeries,
                            timeValue: this.timebucket.value
                        } as SuperChartConfiguration_TimeBucket) ?? 0
                        if (newTime > previousTime) {
                            newCandleTimestamp = newTime
                            candleData = [lastCandle, {
                                ...candle,
                                startTimestamp: newTime,
                                lastTickTimestamp: candle.startTimestamp
                            } as Candle]
                        } else if (candle.startTimestamp > previousLastTickTime && candle.startTimestamp < newTime + timeWidth) {
                            candleData[candleData.length - 1] = {
                                ...candle,
                                startTimestamp: candleData[candleData.length - 1].startTimestamp,
                                lastTickTimestamp: candle.startTimestamp,
                                volume: (candleData[candleData.length - 1].volume ?? 0) + (candle.volume ?? 0)
                            } as Candle
                        }
                        this.securityToCandles[key] = candleData
                        if (security && candleData.length > 0) {
                            const candleChange = getCandleChange(candleData[candleData.length - 2], candleData[candleData.length - 1], security)
                            this.securityToCandleChange[key] = candleChange
                        }
                    })
                    // Adding a candle for securities that have not received a WebSocket update
                    if (newCandleTimestamp) {
                        Object.keys(this.securityToCandles).forEach(key => {
                            let candleData = this.securityToCandles[key].slice(-1)
                            const [lastCandle] = candleData
                            if (newCandleTimestamp && lastCandle.startTimestamp < newCandleTimestamp) {
                                candleData = [lastCandle, {
                                    ...lastCandle,
                                    startTimestamp: newCandleTimestamp,
                                    lastTickTimestamp: newCandleTimestamp,
                                    volume: 0
                                } as Candle]
                                this.securityToCandles[key] = candleData
                                if (this.subscribedSecurities[key].security && candleData.length > 0) {
                                    const candleChange = getCandleChange(candleData[candleData.length - 2], candleData[candleData.length - 1], this.subscribedSecurities[key].security)
                                    this.securityToCandleChange[key] = candleChange
                                }
                            }
                        })
                    }
                    this.update(this.securityToCandleChange)
                }
            })
        }
    }

    private update(update: Record<string, CandleChange>) {
        this.onCandleChangeCallbacks.forEach((callback) => {
            callback(update)
        })
    }

    destroy() {
        const secKeys = Object.keys(this.subscribedSecurities)
        if (this.websocketListnerAdded && this.ws) {
            this.ws.removeListener(this.onWebSocketMessageReceived)
        }
        this.websocketListnerAdded = false
        this.subscribedSecurities = {}
        this.securityToCandles = {}
        this.securityToCandleChange = {}
        this.subscribeSecurityCandles()
        if (secKeys.length) {
            this.dispatch(removeCandleChangeBookEntries(secKeys))
        }
    }
}
